import { AsyncPipe, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { EnergyConsumptionHelper, UnitsConversionHelper } from '@cfp/core';
import { BuildingReportDescriptionService } from '@cfp/shared/building/report';
import { EnergyLabel } from '@cfp/shared/energy-label';
import { ConstructionPeriodDirective } from '@cfp/shared/ui/construction-period';
import { LabelGroupService } from '@cfp/shared/ui/label-group';
import { MeasurementArea } from '@cfp/shared/ui/measurement';
import { TranslateSubObjectTypePipe } from '@cfp/shared/ui/object-type';
import {
  Card,
  Co2Module,
  CoalUnitsConversionPipe,
  CurrencyModule,
  GasUnitsConversionPipe,
  HeatUnitsConversionPipe,
  OilUnitsConversionPipe,
  PelletUnitsConversionPipe,
  SanitizeHtmlPipe,
  WoodUnitsConversionPipe
} from '@cfp/ui';
import { TranslatedRouterLink } from '@cfp/ui/common';
import { TranslateModule } from '@ngx-translate/core';
import { BuildingDetailsDTO, BuildingEnergyIndexDTO, BuildingReportDTO, ObjectTypeDTO } from 'csr-asset-app';
import { combineLatest, switchMap } from 'rxjs';

@Component({
  selector: 'cfp-building-summary-card',
  templateUrl: './building-summary-card.html',
  styleUrls: ['./building-summary-card.scss'],
  imports: [
    DecimalPipe,
    TranslateModule,
    TranslatedRouterLink,
    ConstructionPeriodDirective,
    CurrencyModule,
    EnergyLabel,
    SanitizeHtmlPipe,
    MeasurementArea,
    GasUnitsConversionPipe,
    TranslateSubObjectTypePipe,
    AsyncPipe,
    HeatUnitsConversionPipe,
    OilUnitsConversionPipe,
    WoodUnitsConversionPipe,
    PelletUnitsConversionPipe,
    CoalUnitsConversionPipe,
    Card,
    Co2Module
  ],
  providers: [BuildingReportDescriptionService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BuildingSummaryCard {
  public readonly portfolioId = input.required<number>();
  public readonly buildingId = input.required<number>();
  public readonly building = input.required<BuildingDetailsDTO>();
  public readonly buildingReport = input<BuildingReportDTO>();
  public readonly header = input<string>('summaryOfYourBuilding');
  public readonly showLabel = input<boolean>(true);
  public readonly showSubTitle = input<boolean>(true);
  public readonly showDescription = input<boolean>(true);
  public readonly showObjectType = input<boolean>(true);
  public readonly showSubObjectType = input<boolean>(false);
  public readonly showEnergyCosts = input<boolean>(true);
  public readonly showCo2Consumption = input<boolean>(false);
  public readonly textColor = input<string>('text-muted');
  public readonly fragment = input<string>('energylabel');
  public readonly buildingLabel = input<BuildingEnergyIndexDTO>();

  protected readonly ObjectTypeDTO = ObjectTypeDTO;
  protected readonly energyConsumptionHelper = inject(EnergyConsumptionHelper);

  protected readonly labelGroup = computed(() => {
    const buildingLabel = this.buildingLabel();
    if (!buildingLabel?.currentLabel) {
      return undefined;
    }
    return this.labelGroupService.mapIndexTypeToLabelGroup(buildingLabel.currentLabel.indexType);
  });

  protected readonly defaultBuildingReportDescription = toSignal(
    combineLatest([toObservable(this.building), toObservable(this.buildingLabel), toObservable(this.labelGroup)]).pipe(
      switchMap(([building, buildingLabel, labelGroup]) =>
        this.buildingReportDescriptionService.getDefaultBuildingReportDescription(building, buildingLabel, labelGroup)
      )
    )
  );

  protected readonly labelType = computed(() => {
    if (this.buildingLabel().currentLabel?.registrationDate) {
      return 'certified';
    }
    return 'calculated';
  });

  protected readonly unitsConversionHelper = inject(UnitsConversionHelper);
  private readonly labelGroupService = inject(LabelGroupService);
  private readonly buildingReportDescriptionService = inject(BuildingReportDescriptionService);

  protected scrollToFragment(): void {
    const fragmentElement = document.querySelector('#measures');
    if (fragmentElement) {
      fragmentElement.scrollIntoView({ behavior: 'smooth' });
    }
  }
}
